import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import ProductCard from "../components/storefront/ProductCard";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../components/ui/sheet";
import { Input } from "../components/ui/input";
import { CLOTHING_TYPES, SIZES, formatFCFA, imageUrl } from "../lib/format";
import { SlidersHorizontal, Search, X } from "lucide-react";

function FilterControls({ type, setType, taille, setTaille, etat, setEtat, facets, prix, setPrix, apply }) {
  return (
    <div className="space-y-6">
      <div>
        <label className="mb-2 block text-sm font-medium text-[#2C1A14]">Type de vêtement</label>
        <Select value={type} onValueChange={setType}>
          <SelectTrigger data-testid="filter-type" className="bg-white"><SelectValue placeholder="Tous" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            {CLOTHING_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="mb-2 block text-sm font-medium text-[#2C1A14]">Taille</label>
        <Select value={taille} onValueChange={setTaille}>
          <SelectTrigger data-testid="filter-taille" className="bg-white"><SelectValue placeholder="Toutes" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes</SelectItem>
            {(facets.tailles.length ? facets.tailles : SIZES).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="mb-2 block text-sm font-medium text-[#2C1A14]">État</label>
        <Select value={etat} onValueChange={setEtat}>
          <SelectTrigger data-testid="filter-etat" className="bg-white"><SelectValue placeholder="Tous" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous</SelectItem>
            {facets.etats.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <label className="mb-2 block text-sm font-medium text-[#2C1A14]">
          Prix maximum : {formatFCFA(prix[1])}
        </label>
        <Slider
          data-testid="filter-prix"
          min={0} max={60000} step={1000}
          value={[prix[1]]} onValueChange={(v) => setPrix([0, v[0]])}
          className="mt-3"
        />
      </div>
      <Button data-testid="apply-filters" onClick={apply} className="w-full rounded-full bg-[#D76E4D] text-white hover:bg-[#C05A3A]">
        Appliquer
      </Button>
    </div>
  );
}

export default function Catalog() {
  const [params] = useSearchParams();
  const tag = params.get("tag") || "";
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState("recent");
  const [type, setType] = useState("all");
  const [taille, setTaille] = useState("all");
  const [etat, setEtat] = useState("all");
  const [facets, setFacets] = useState({ tailles: [], etats: [] });
  const [suggest, setSuggest] = useState([]);
  const [focused, setFocused] = useState(false);
  const [prix, setPrix] = useState([0, 60000]);
  const [applied, setApplied] = useState({ type: "all", taille: "all", etat: "all", prix: [0, 60000] });
  const [search, setSearch] = useState("");
  const [qSearch, setQSearch] = useState("");

  useEffect(() => {
    api.get("/products/facets").then((r) => setFacets({ tailles: r.data?.tailles || [], etats: r.data?.etats || [] })).catch(() => {});
  }, []);

  useEffect(() => {
    const q = search.trim();
    if (q.length < 2) { setSuggest([]); return undefined; }
    const t = setTimeout(() => {
      api.get("/products", { params: { search: q, limit: 5 } }).then((r) => setSuggest(r.data?.items || [])).catch(() => {});
    }, 200);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(() => setQSearch(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchPage = useCallback((pageNum, reset) => {
    setLoading(true);
    const p = {
      page: pageNum, limit: 12, sort,
      ...(tag ? { tag } : {}),
      ...(qSearch ? { search: qSearch } : {}),
      ...(applied.type !== "all" ? { type: applied.type } : {}),
      ...(applied.taille !== "all" ? { taille: applied.taille } : {}),
      ...(applied.etat !== "all" ? { etat: applied.etat } : {}),
      prix_min: applied.prix[0], prix_max: applied.prix[1],
    };
    api.get("/products", { params: p })
      .then((r) => {
        setItems((prev) => (reset ? r.data.items : [...prev, ...r.data.items]));
        setPages(r.data.pages);
        setTotal(r.data.total);
      })
      .finally(() => setLoading(false));
  }, [sort, tag, applied, qSearch]);

  useEffect(() => {
    setPage(1);
    fetchPage(1, true);
  }, [fetchPage]);

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    fetchPage(next, false);
  };

  const apply = () => setApplied({ type, taille, etat, prix });

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8 lg:py-12">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="whitespace-nowrap font-serif text-3xl font-medium text-[#2C1A14] sm:text-4xl">
            {tag === "nouveau" ? "Nouveautés" : "Le Dressing"}
          </h1>
          <p className="mt-1 text-sm text-[#9B7E73]">{total} pièce{total > 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Mobile filter */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" data-testid="mobile-filter-button" className="lg:hidden border-[#E8D5CE] bg-white">
                <SlidersHorizontal className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-[#FBF1EE]">
              <SheetHeader><SheetTitle className="font-serif text-[#2C1A14]">Filtres</SheetTitle></SheetHeader>
              <div className="mt-6">
                <FilterControls {...{ type, setType, taille, setTaille, etat, setEtat, facets, prix, setPrix, apply }} />
              </div>
            </SheetContent>
          </Sheet>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger data-testid="sort-select" className="w-[180px] bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">Plus récent</SelectItem>
              <SelectItem value="prix_asc">Prix croissant</SelectItem>
              <SelectItem value="prix_desc">Prix décroissant</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="relative mb-6">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#9B7E73]" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          data-testid="catalog-search"
          placeholder="Rechercher une pièce (nom, style, mot-clé)…"
          className="h-12 rounded-full border-[#E8D5CE] bg-white pl-12 pr-12 text-base shadow-sm focus-visible:ring-[#D76E4D]"
        />
        {search && (
          <button onClick={() => setSearch("")} data-testid="catalog-search-clear" className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9B7E73] hover:text-[#D76E4D]">
            <X className="h-5 w-5" />
          </button>
        )}
        {focused && suggest.length > 0 && (
          <div className="absolute left-0 right-0 top-full z-30 mt-2 overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-[#E8D5CE]" data-testid="search-suggestions">
            {suggest.map((p) => (
              <Link key={p.id} to={`/produit/${p.id}`} data-testid={`suggestion-${p.id}`} className="flex items-center gap-3 px-4 py-2.5 transition-colors hover:bg-[#FBF1EE]">
                <img src={imageUrl(p.images?.[0], 320)} alt="" className="h-12 w-10 rounded object-cover" />
                <span className="min-w-0 flex-1 truncate text-sm text-[#2C1A14]">{p.nom}</span>
                <span className="shrink-0 text-sm font-medium text-[#D76E4D]">{formatFCFA(p.prix)}</span>
              </Link>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-8">
        {/* Desktop sidebar */}
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-24 rounded-xl bg-white p-5 ring-1 ring-[#E8D5CE]">
            <FilterControls {...{ type, setType, taille, setTaille, etat, setEtat, facets, prix, setPrix, apply }} />
          </div>
        </aside>

        <div className="flex-1">
          {items.length === 0 && !loading ? (
            <p className="py-20 text-center text-[#9B7E73]">Aucune pièce ne correspond à votre recherche.</p>
          ) : (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 sm:gap-6">
              {items.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}
          {page < pages && (
            <div className="mt-10 text-center">
              <Button
                onClick={loadMore}
                disabled={loading}
                data-testid="load-more-button"
                className="rounded-full bg-[#2C1A14] px-8 text-white hover:bg-[#3d251c]"
              >
                {loading ? "Chargement..." : "Charger plus"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
